import { apiGet, apiPost, apiPut } from './api-client'
import type {
  AuditRecord,
  DraftBoard,
  DraftSlot,
  FantasyProvider,
  LeagueConnection,
  Player,
  RiskPreferences,
} from '../types/fantasy-football'
import {
  defaultParams,
  defaultRiskPreferences,
  parameterSnapshot,
  type ParamMap,
} from './fantasy-knowledge-base'
import { LEAGUE_OPPONENTS, MOCK_ROSTER_IDS, PLAYER_POOL, SOS } from './fantasy-data'
import type { SosTable } from './fantasy-analytics'

const USE_MOCK = !import.meta.env.VITE_FANTASY_API_URL

let _usingMockData = USE_MOCK
export function isFantasyUsingMockData(): boolean {
  return _usingMockData
}

/** True once VITE_FANTASY_API_URL points at a deployed fantasy backend. */
export function isFantasyBackendConfigured(): boolean {
  return !USE_MOCK
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

async function call<T>(real: () => Promise<T>, mock: () => T, ms = 250): Promise<T> {
  if (!USE_MOCK) {
    try {
      const result = await real()
      _usingMockData = false
      return result
    } catch {
      console.warn('Fantasy Football API unavailable, falling back to mock data')
      _usingMockData = true
    }
  }
  _usingMockData = true
  await delay(ms)
  return mock()
}

// ─── Mock store (localStorage-backed) ────────────────────────

const MOCK_KEY = 'nb-fantasy-mock-v1'

interface MockState {
  connections: LeagueConnection[]
  rosterIds: string[]
  acquired: string[]
  dropped: string[]
  prefs: RiskPreferences
  params: ParamMap
  draftBoard: DraftBoard | null
  auditLog: AuditRecord[]
}

let _memoryState: MockState | null = null

function seedConnections(): LeagueConnection[] {
  const twoHoursAgo = new Date(Date.now() - 2 * 3600000).toISOString()
  return [
    {
      id: 'conn-yahoo-family',
      provider: 'yahoo',
      status: 'connected',
      leagueName: 'Brown Family League',
      leagueId: '414.l.88213',
      teamName: "Nathanael's Nine",
      teamCount: 10,
      scoring: 'half-ppr',
      lastSync: twoHoursAgo,
    },
  ]
}

function seedState(): MockState {
  return {
    connections: seedConnections(),
    rosterIds: [...MOCK_ROSTER_IDS],
    acquired: [],
    dropped: [],
    prefs: defaultRiskPreferences(),
    params: defaultParams(),
    draftBoard: null,
    auditLog: [],
  }
}

function loadState(): MockState {
  if (_memoryState) return _memoryState
  try {
    const raw = localStorage.getItem(MOCK_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as MockState
      // Merge new defaults so added formulas/prefs don't break saved stores.
      parsed.prefs = { ...defaultRiskPreferences(), ...parsed.prefs }
      parsed.params = { ...defaultParams(), ...parsed.params }
      _memoryState = parsed
      return _memoryState
    }
  } catch {
    // corrupted store — reseed
  }
  _memoryState = seedState()
  saveState(_memoryState)
  return _memoryState
}

function saveState(state: MockState) {
  _memoryState = state
  try {
    localStorage.setItem(MOCK_KEY, JSON.stringify(state))
  } catch {
    // storage unavailable — keep in memory only
  }
}

export function resetFantasyMockData(): void {
  saveState(seedState())
}

function audit(state: MockState, record: Omit<AuditRecord, 'id' | 'timestamp'>) {
  state.auditLog.unshift({
    ...record,
    id: `audit-${Date.now()}-${state.auditLog.length}`,
    timestamp: new Date().toISOString(),
  })
  state.auditLog = state.auditLog.slice(0, 200)
}

// ─── Static reference data ───────────────────────────────────

export function getSosTable(): SosTable {
  return SOS
}

export function getLeagueOpponents(): string[] {
  return LEAGUE_OPPONENTS
}

// ─── League connections ──────────────────────────────────────

export async function listConnections(): Promise<LeagueConnection[]> {
  return call(
    () => apiGet<LeagueConnection[]>('/api/fantasy/connections'),
    () => loadState().connections,
  )
}

// Real mode starts a provider OAuth flow (Yahoo OAuth 2.0 / ESPN) via the
// backend and redirects the browser; mock mode simulates a completed link.
export async function connectProvider(provider: FantasyProvider): Promise<LeagueConnection> {
  if (!USE_MOCK) {
    const { authorizeUrl } = await apiPost<{ authorizeUrl: string }>(
      `/api/fantasy/connections/${provider}/authorize`,
    )
    _usingMockData = false
    window.location.href = authorizeUrl
    return new Promise<LeagueConnection>(() => {}) // navigation takes over
  }
  await delay(1400)
  const state = loadState()
  const now = new Date().toISOString()
  const conn: LeagueConnection =
    provider === 'yahoo'
      ? {
          id: `conn-yahoo-${Date.now()}`,
          provider: 'yahoo',
          status: 'connected',
          leagueName: 'Yahoo Public League',
          leagueId: `414.l.${Math.floor(10000 + Math.random() * 89999)}`,
          teamName: "Nathanael's Nine",
          teamCount: 10,
          scoring: 'half-ppr',
          lastSync: now,
        }
      : {
          id: `conn-espn-${Date.now()}`,
          provider: 'espn',
          status: 'connected',
          leagueName: 'The Office League',
          leagueId: `${Math.floor(100000 + Math.random() * 899999)}`,
          teamName: 'Brown Bombers',
          teamCount: 12,
          scoring: 'ppr',
          lastSync: now,
        }
  state.connections.push(conn)
  audit(state, {
    action: 'league-synced',
    detail: `Connected ${provider === 'yahoo' ? 'Yahoo' : 'ESPN'} league "${conn.leagueName}" (${conn.teamCount} teams)`,
    formulaIds: [],
    parameterSnapshot: {},
  })
  saveState(state)
  return conn
}

// Called by the /fantasy/yahoo/callback route. The backend owns the client
// secret and validates the `state` it issued, so the browser only relays.
export async function completeYahooConnect(
  code: string,
  state: string | null,
): Promise<LeagueConnection> {
  const conn = await apiPost<LeagueConnection>('/api/fantasy/connections/yahoo/callback', {
    code,
    state,
  })
  _usingMockData = false
  return conn
}

export async function syncConnection(id: string): Promise<LeagueConnection> {
  return call(
    () => apiPost<LeagueConnection>(`/api/fantasy/connections/${encodeURIComponent(id)}/sync`),
    () => {
      const state = loadState()
      const conn = state.connections.find((c) => c.id === id)
      if (!conn) throw new Error('Connection not found')
      conn.lastSync = new Date().toISOString()
      conn.status = 'connected'
      audit(state, {
        action: 'league-synced',
        detail: `Synced "${conn.leagueName}" — rosters and available players refreshed`,
        formulaIds: [],
        parameterSnapshot: {},
      })
      saveState(state)
      return conn
    },
    1200,
  )
}

export async function disconnectProvider(id: string): Promise<void> {
  return call(
    () => apiPost<void>(`/api/fantasy/connections/${encodeURIComponent(id)}/disconnect`),
    () => {
      const state = loadState()
      state.connections = state.connections.filter((c) => c.id !== id)
      saveState(state)
    },
  )
}

// ─── Players & roster ────────────────────────────────────────

export async function listPlayers(): Promise<Player[]> {
  return call(
    () => apiGet<Player[]>('/api/fantasy/players'),
    () => {
      const state = loadState()
      return PLAYER_POOL.map((p) => {
        if (state.acquired.includes(p.id)) return { ...p, rosterStatus: 'mine' as const, ownedByTeam: undefined }
        if (state.dropped.includes(p.id)) return { ...p, rosterStatus: 'available' as const, ownedByTeam: undefined }
        return p
      })
    },
    350,
  )
}

export async function getRosterIds(): Promise<string[]> {
  return call(
    () => apiGet<string[]>('/api/fantasy/roster'),
    () => loadState().rosterIds,
    100,
  )
}

/** Execute a user-approved add/drop. Mock mode mutates local roster state. */
export async function acquirePlayer(playerId: string, dropPlayerId: string | null): Promise<void> {
  return call(
    () => apiPost<void>('/api/fantasy/roster/acquire', { playerId, dropPlayerId }),
    () => {
      const state = loadState()
      if (!state.rosterIds.includes(playerId)) state.rosterIds.push(playerId)
      state.acquired = [...new Set([...state.acquired, playerId])]
      state.dropped = state.dropped.filter((id) => id !== playerId)
      if (dropPlayerId) {
        state.rosterIds = state.rosterIds.filter((id) => id !== dropPlayerId)
        state.dropped = [...new Set([...state.dropped, dropPlayerId])]
        state.acquired = state.acquired.filter((id) => id !== dropPlayerId)
      }
      const added = PLAYER_POOL.find((p) => p.id === playerId)?.name ?? playerId
      const droppedName = dropPlayerId
        ? PLAYER_POOL.find((p) => p.id === dropPlayerId)?.name ?? dropPlayerId
        : null
      audit(state, {
        action: 'player-acquired',
        detail: droppedName ? `Acquired ${added}, dropped ${droppedName}` : `Acquired ${added}`,
        formulaIds: ['F8', 'F10', 'F11'],
        parameterSnapshot: parameterSnapshot(state.prefs, state.params),
      })
      saveState(state)
    },
    500,
  )
}

// ─── Risk preferences & formula parameters ───────────────────

export async function getRiskPreferences(): Promise<RiskPreferences> {
  return call(
    () => apiGet<RiskPreferences>('/api/fantasy/preferences'),
    () => loadState().prefs,
    100,
  )
}

export async function saveRiskPreferences(prefs: RiskPreferences): Promise<void> {
  return call(
    () => apiPut<void>('/api/fantasy/preferences', prefs),
    () => {
      const state = loadState()
      state.prefs = prefs
      audit(state, {
        action: 'preferences-changed',
        detail: 'Risk preferences updated (risk aversion, scarcity weights, source trust)',
        formulaIds: ['F1', 'F4', 'F6', 'F7', 'F8', 'F9'],
        parameterSnapshot: parameterSnapshot(prefs, state.params),
      })
      saveState(state)
    },
    150,
  )
}

export async function getParams(): Promise<ParamMap> {
  return call(
    () => apiGet<ParamMap>('/api/fantasy/parameters'),
    () => loadState().params,
    100,
  )
}

export async function saveParams(params: ParamMap, changedFormulaIds: string[]): Promise<void> {
  return call(
    () => apiPut<void>('/api/fantasy/parameters', params),
    () => {
      const state = loadState()
      state.params = params
      audit(state, {
        action: 'parameters-changed',
        detail: `Formula parameters updated (${changedFormulaIds.join(', ') || 'no formula ids'})`,
        formulaIds: changedFormulaIds,
        parameterSnapshot: parameterSnapshot(state.prefs, params),
      })
      saveState(state)
    },
    150,
  )
}

// ─── Draft board ─────────────────────────────────────────────

export async function getDraftBoard(): Promise<DraftBoard | null> {
  return call(
    () => apiGet<DraftBoard | null>('/api/fantasy/draft-board'),
    () => loadState().draftBoard,
    100,
  )
}

/** Persist a freshly generated (F12) board as "proposed" pending approval. */
export async function saveProposedDraftBoard(slots: DraftSlot[]): Promise<DraftBoard> {
  return call(
    () => apiPost<DraftBoard>('/api/fantasy/draft-board', { slots }),
    () => {
      const state = loadState()
      const board: DraftBoard = {
        id: `board-${Date.now()}`,
        generatedAt: new Date().toISOString(),
        approvedAt: null,
        status: 'proposed',
        slots,
      }
      state.draftBoard = board
      audit(state, {
        action: 'draft-order-generated',
        detail: `Draft order proposed — ${slots.length} slots (greedy F12 simulation)`,
        formulaIds: ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F12'],
        parameterSnapshot: parameterSnapshot(state.prefs, state.params),
      })
      saveState(state)
      return board
    },
    300,
  )
}

/** User reorder of the proposed board before approving. */
export async function updateDraftBoardSlots(slots: DraftSlot[]): Promise<DraftBoard> {
  return call(
    () => apiPut<DraftBoard>('/api/fantasy/draft-board', { slots }),
    () => {
      const state = loadState()
      if (!state.draftBoard) throw new Error('No draft board to update')
      state.draftBoard = { ...state.draftBoard, slots, status: 'proposed', approvedAt: null }
      saveState(state)
      return state.draftBoard
    },
    100,
  )
}

/** Explicit user approval turns the proposal into the active draft order. */
export async function approveDraftBoard(): Promise<DraftBoard> {
  return call(
    () => apiPost<DraftBoard>('/api/fantasy/draft-board/approve'),
    () => {
      const state = loadState()
      if (!state.draftBoard) throw new Error('No draft board to approve')
      state.draftBoard = {
        ...state.draftBoard,
        status: 'approved',
        approvedAt: new Date().toISOString(),
      }
      audit(state, {
        action: 'draft-order-approved',
        detail: `Draft order approved by user — ${state.draftBoard.slots.length} slots locked in`,
        formulaIds: ['F12'],
        parameterSnapshot: parameterSnapshot(state.prefs, state.params),
      })
      saveState(state)
      return state.draftBoard
    },
    300,
  )
}

// ─── Audit log ───────────────────────────────────────────────

export async function listAuditRecords(): Promise<AuditRecord[]> {
  return call(
    () => apiGet<AuditRecord[]>('/api/fantasy/audit'),
    () => loadState().auditLog,
    100,
  )
}
